/* Enhance the automatically loaded native Aissie widget; keep its click handlers. */
(() => {
  const icon = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="%23ffffff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z"/></svg>';
  const deadline = performance.now() + 20000;

  function enhance() {
    const shadow = document.getElementById('aissie-chat')?.shadowRoot;
    if (!shadow) {
      // The vendor attaches its shadow root asynchronously. Stop if it fails to load.
      if (performance.now() < deadline) requestAnimationFrame(enhance);
      return;
    }

    const style = document.createElement('style');
    style.textContent = `
      #chat-widget [data-gp-chat-launcher]:not(#chat-header) {
        bottom: 20px;
      }
      #chat-widget [data-gp-chat-icon] { position: relative; }
      #chat-widget [data-gp-chat-icon],
      #chat-header,
      #chat-widget button[aria-label="Send message"] {
        background-color: #566449 !important;
        color: #ffffff !important;
      }
      #chat-widget [data-gp-chat-icon] > img { display: none; }
      #chat-widget [data-gp-chat-icon]::after {
        content: "";
        position: absolute;
        inset: 0;
        margin: auto;
        width: 28px;
        height: 28px;
        background: center / contain no-repeat url('data:image/svg+xml,${icon}');
        pointer-events: none;
      }
      #chat-widget [data-gp-chat-icon]:focus-visible {
        outline: 3px solid #232721;
        outline-offset: 4px;
        border-radius: 999px;
      }
      #chat-widget {
        overflow: visible;
        top: 0;
        transform: translateY(calc(100dvh - 100% - var(--gp-sticky-cart-height, 0px) - var(--gp-chat-bottom-gap, 0px)));
        max-height: calc(100dvh - var(--gp-sticky-cart-height, 0px) - var(--gp-chat-bottom-gap, 0px) - max(12px, env(safe-area-inset-top)));
      }
      #chat-widget:has(#chat-header) {
        overflow: hidden;
        height: min(var(--gp-chat-panel-height, 486px), calc(100dvh - var(--gp-sticky-cart-height, 0px) - var(--gp-chat-bottom-gap, 0px) - max(12px, env(safe-area-inset-top))));
      }
      #chat-header { top: auto; bottom: auto; flex-shrink: 0; }
      #chat-body { min-height: 0; }
      #chat-widget [aria-label="Dismiss"] {
        min-width: 32px;
        min-height: 32px;
        padding: 4px;
        margin-left: 4px;
      }
      @media (min-height: 760px) {
        #chat-widget { --gp-chat-panel-height: 586px; }
      }
      @media (min-height: 1000px) {
        #chat-widget { --gp-chat-panel-height: 686px; }
      }
      @media (max-width: 767px) {
        #chat-widget {
          --gp-chat-left-gap: max(12px, env(safe-area-inset-left));
          --gp-chat-right-gap: max(12px, env(safe-area-inset-right));
          --gp-chat-bottom-gap: max(12px, env(safe-area-inset-bottom));
          box-sizing: border-box;
          width: calc(100% - var(--gp-chat-left-gap) - var(--gp-chat-right-gap));
          min-width: 0;
          max-width: 400px;
          left: auto;
          right: var(--gp-chat-right-gap);
          border-radius: 12px;
        }
        #chat-widget [data-gp-chat-launcher]:not(#chat-header) { right: 8px; }
        #chat-header, #chat-body, #chat-footer, #footer {
          min-width: 0;
        }
        #chat-input {
          min-width: 0;
          max-width: 100%;
          font-size: 16px;
        }
      }
    `;
    shadow.appendChild(style);

    // The "Questions?" teaser covers buttons and copy at the bottom-right of the first screen
    // on most pages. Keep only the round launcher.
    const teaserStyle = document.createElement('style');
    teaserStyle.textContent = `
      #chat-widget [data-gp-chat-launcher] > div:has(> button[aria-label="Dismiss"]) { display: none !important; }
    `;
    shadow.appendChild(teaserStyle);

    // Step the launcher aside while it would sit on top of an add-to-cart button.
    const ctaSelector = 'button[name="add"], .button--add-to-cart, .gp-frame-mobile-sticky-actions__add, [data-gp-chat-avoid]';
    let avoidQueued = false;
    function avoidButtons() {
      avoidQueued = false;
      const launcher = shadow.querySelector('[data-gp-chat-launcher]');
      if (!launcher) return;
      // Visibility doesn't affect layout, so the launcher's box is the same either way.
      const box = launcher.getBoundingClientRect();
      const covers = !shadow.getElementById('chat-header') && Array.from(document.querySelectorAll(ctaSelector)).some(button => {
        const rect = button.getBoundingClientRect();
        return rect.width > 0 && rect.bottom > box.top && rect.top < box.bottom && rect.right > box.left && rect.left < box.right;
      });
      // Only write when the state changes, so the style observer doesn't loop.
      const visibility = covers ? 'hidden' : '';
      if (launcher.style.visibility !== visibility) launcher.style.visibility = visibility;
    }
    function queueAvoid() {
      if (avoidQueued) return;
      avoidQueued = true;
      requestAnimationFrame(avoidButtons);
    }
    window.addEventListener('scroll', queueAvoid, { passive: true });
    window.addEventListener('resize', queueAvoid);

    function sync() {
      const widget = shadow.getElementById('chat-widget');
      // Keep the layout anchor fixed; the transform positions the panel above
      // the sticky cart without moving its layout box as messages load.
      const bottom = 'auto';
      if (widget && widget.style.bottom !== bottom) {
        widget.style.bottom = bottom;
      }
      const image = shadow.querySelector('img[alt="Open chat"]');
      if (image && !image.parentElement.hasAttribute('data-gp-chat-icon')) {
        const iconButton = image.parentElement;
        const launcher = iconButton.parentElement;
        iconButton.setAttribute('data-gp-chat-icon', '');
        iconButton.setAttribute('role', 'button');
        iconButton.setAttribute('aria-label', 'Open chat');
        iconButton.tabIndex = 0;
        launcher.setAttribute('data-gp-chat-launcher', '');
        launcher.setAttribute('role', 'group');
        launcher.setAttribute('aria-label', 'Chat controls');
        launcher.removeAttribute('tabindex');
        iconButton.addEventListener('keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          // Reuse the native launcher's click handler.
          launcher.click();
        });
      }
    }

    const observer = new MutationObserver(() => { sync(); queueAvoid(); });
    observer.observe(shadow, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    sync();
    queueAvoid();
  }

  enhance();
})();
